import "./style.scss";

import { Button, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from "@mui/material";
import AgreementModal from "components/shared/AgreementModal";
import TableSkeletonRows from "components/shared/TableSkeletonRows";
import ThreeDotMenu from "components/shared/ThreeDotMenu";
import type { IBeneficiar } from "containers/Beneficiars/types";
import type { NewAgreementLocationState } from "containers/ContractValidationNew";
import type { ITitle } from "containers/Titles/types";
import { formatLocaleDateString } from "helpers/utils";
import usePaymentStateUpdate from "hooks/usePaymentStateUpdate";
import { useCompanySettings } from "hooks/useCompanySettings";
import { useActiveSignProvider } from "hooks/useActiveSignProvider";
import { useSignatureProvider } from "hooks/useSignatureProvider";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import type { TermGroup } from "store/beneficiaryStore";
import { fetchTermAssociatedToBeneficiaries } from "store/beneficiaryStore";
import type { ITerm } from "store/managementStore";
import type { IBeneficiaryPayment } from "store/paymentSlice";
import { EPaymentResourceType, fetchResourcePayment } from "store/paymentSlice";
import type { ICompany } from "store/settingsStore";
import type { AppDispatch } from "store/store";
import { RootState, useAppSelector } from "store/store";
import { deleteTerm } from "store/termStore";
import { fetchTitle } from "store/titleStore";

import variables from "../../../assets/styles/_variables.module.scss";
import DeleteTermModal from "../DeleteTermModal";
import InformationTitle from "../InformationTitle";
import ShowDetails from "../ShowDetails";
import TextButton from "../TextButton";
import TitleCover from "../TitleCover";
import RenewTermModal from "../TitlesTerms/RenewTermModal";
import { getRenewalPeriods, isTermRenewable, TermExpirationStatusWrapper } from "../TitlesTerms/TableColumnsFormat";

interface AssociatedTitleType extends ITitle {
    isDeleted: number;
    titleId: number;
}

interface IBeneficiariesTerms {
    title: string;
    currentBeneficiary: IBeneficiar;
    currentBeneficiarName: string;
    currentBeneficiarId: number;
    company: ICompany | undefined;
    usesAdvanceCompensation: boolean;
    termGroups: TermGroup[];
    usesMultiPeriod: boolean;
    isLoading?: boolean;
}

const tHeadStyles = {
    maxWidth: 200,
    fontWeight: 600,
    fontSize: "12px",
    color: "#676767",
    width: "min-content",
    padding: "8px 16px",
    backgroundColor: "#F6F6F6",
    fontFamily: "'Poppins', sans-serif",
    whiteSpace: "nowrap",
    zIndex: "0",
};

/**
 * Terms linked to beneficiaries (we see a list of titles)
 */
const BeneficiariesTerms = (props: IBeneficiariesTerms) => {
    const {
        title,
        currentBeneficiarName,
        currentBeneficiary,
        currentBeneficiarId,
        company,
        usesAdvanceCompensation,
        termGroups,
        usesMultiPeriod,
        isLoading = false,
    } = props;

    const [t] = useTranslation();
    const dispatch = useDispatch<AppDispatch>();
    const navigate = useNavigate();
    const location = useLocation();

    const { getCompanySettings } = useCompanySettings();
    const usesAgreements = getCompanySettings("uses_agreements");
    const usesAgreementValidation = getCompanySettings("uses_agreement_validation_chain");

    const activeSignProvider = useActiveSignProvider();
    const { isDocoon } = useSignatureProvider(activeSignProvider.key);

    const { termAssociatedToBeneficiaries: terms } = useAppSelector((state) => state.beneficiary);
    const { periodsDateRange } = useAppSelector((state) => state.period);

    const { updatePaymentPageState } = usePaymentStateUpdate(periodsDateRange);

    const [isOpen, setIsOpen] = useState(false);
    const [currentTerm, setCurrentTerm] = useState<ITerm>();
    const [isDeleteTermModalOpen, setIsDeleteTermModalOpen] = useState(false);
    const [termIdToDelete, setTermIdToDelete] = useState<number>();
    const [advancePaymentId, setAdvancePaymentId] = useState<number | null>(null);
    const [showRenewTermModal, setShowRenewTermModal] = useState(false);
    const [resourcePayment, setResourcePayment] = useState<IBeneficiaryPayment[]>([]);
    const [avenantTerm, setAvenantTerm] = useState<ITerm | null>(null);

    // Needed to figure out the period duration per title.
    const { periods, activePeriod } = useAppSelector((state) => state.period);

    const sortedTermsByGroupId = useMemo(() => {
        const sortedTerms = [...terms]
            .sort(
                (a, b) =>
                    (a?.compensationGroupId ? a?.compensationGroupId : -1) -
                    (b?.compensationGroupId ? b?.compensationGroupId : -1),
            )
            .reverse();
        return usesAdvanceCompensation ? sortedTerms : terms;
    }, [terms, usesAdvanceCompensation]);

    const tableColumnCount = 4 + (usesAdvanceCompensation ? 1 : 0) + (usesMultiPeriod ? 1 : 0);

    const isTableLoading = isLoading || (!!sortedTermsByGroupId.length && !activePeriod);

    useEffect(() => {
        if (!location.state || !terms.length) {
            return;
        }
        const currTerm = terms.find((term) => term.id === location.state.termId);
        if (currTerm) {
            setCurrentTerm(currTerm);
            setIsOpen(true);
            if (currTerm.titleId != null) {
                dispatch(fetchTitle(currTerm.titleId.toString()));
            }
        }

        setAdvancePaymentId(location.state.advancePaymentId ?? null);
    }, [location, terms]);

    const openRelatedResource = useCallback(
        (term: { workId?: number | null; titleId?: number | null }) => {
            if (term.workId) {
                navigate(`/works/${term.workId}?filterList=1`);
                return;
            }
            if (term.titleId) {
                navigate(`/titles/${term.titleId}`);
            }
        },
        [navigate],
    );

    const handleShowDetails = (term: ITerm) => {
        setCurrentTerm(term);
        if (term.id !== currentTerm?.id && term.titleId != null) {
            dispatch(fetchTitle(term.titleId.toString()));
        }
        setIsOpen(true);
    };

    const handleCreateAvenant = (term: ITerm) => {
        if (usesAgreementValidation || isDocoon) {
            const state: NewAgreementLocationState = {
                relatedResourceData: {
                    relatedResource: "term",
                    relatedResourceId: term.id,
                    beneficiaryId: (term as any).beneficiarId,
                    titleId: term.titleId,
                    // The work loads its terms, and the email pre-fills the Docoon main signatory
                    workId: term.workId,
                    email: currentBeneficiary?.email ?? undefined,
                    title: (term as any).title,
                    beneficiary: (term as any).author,
                },
                modalType: "single",
                documentType: "avenant",
                signatureProvider: activeSignProvider.key,
            };
            navigate("/agreements/new", { state });
            return;
        }
        setAvenantTerm(term);
    };

    const handleDeleteTerm = async (termId: number) => {
        const response = await dispatch(deleteTerm(termId));
        if (response.meta.requestStatus === "fulfilled") {
            setIsDeleteTermModalOpen(false);
            setIsOpen(false);
            setTermIdToDelete(undefined);

            await dispatch(
                fetchTermAssociatedToBeneficiaries({
                    beneficiaryId: currentBeneficiarId,
                }),
            );

            await updatePaymentPageState();
        }
    };

    const handleOpenDeleteModal = async (termId: number) => {
        await dispatch(
            fetchResourcePayment({
                relatedResourceId: termId,
                resourceType: EPaymentResourceType.BENEFICIARY,
            }),
        ).then((res) => {
            if (res.meta.requestStatus === "fulfilled") {
                setResourcePayment(res.payload);
            }
        });
        setIsDeleteTermModalOpen(true);
        setTermIdToDelete(termId);
    };

    const termToDelete = useMemo(() => {
        const term = terms.find((elm) => elm.id === termIdToDelete);
        if (term) {
            return {
                title: term.title,
                beneficiar: currentBeneficiarName,
                representative: term.representative || "-",
            };
        }
        return null;
    }, [termIdToDelete, terms]);

    const renderButtons = useCallback(
        (associatedTitle: AssociatedTitleType) => {
            const canDeleteTerm = !associatedTitle.isDeleted;

            return (
                <TableCell style={{ textAlign: "right" }}>
                    <Button
                        variant="text"
                        sx={{
                            color: "#4C93E6",
                            fontSize: "11px",
                            textDecoration: "underline",
                            textTransform: "none",
                            fontFamily: "Poppins",
                            whiteSpace: "nowrap",
                        }}
                        onClick={() => handleShowDetails(associatedTitle as any)}
                        id={`show-details-term-${associatedTitle?.id}-btn`}
                    >
                        {t("Titles.ShowDetails")}
                    </Button>
                </TableCell>
            );
        },
        [terms, currentBeneficiarName, t, handleShowDetails, deleteTerm],
    );
    const tBodyStyle = {
        fontWeight: 400,
        fontSize: "0.75rem",
        fontFamily: "'Poppins', sans-serif",
        borderColor: "#eaeaea",
    };

    const handleShowRenewTermModal = (e, term: ITerm) => {
        e.stopPropagation();
        setCurrentTerm(term);
        setShowRenewTermModal(true);
    };

    const renderTitles = useCallback(() => {
        if (sortedTermsByGroupId.length > 0 && activePeriod) {
            return sortedTermsByGroupId.map((associatedTitle: any, i) => {
                const compensationGroupName = associatedTitle?.compensationGroupId
                    ? termGroups.find((item) => item.id === associatedTitle?.compensationGroupId)?.name
                    : "";

                const isPreviousLineCompensationGroup =
                    associatedTitle?.compensationGroupId &&
                    sortedTermsByGroupId[i - 1]?.compensationGroupId === associatedTitle?.compensationGroupId;

                const isNextLineCompensationGroup =
                    associatedTitle?.compensationGroupId &&
                    sortedTermsByGroupId[i + 1]?.compensationGroupId === associatedTitle?.compensationGroupId;

                const titlePeriodDuration = periods.find(
                    (period) => period.periodTypeId === associatedTitle?.periodTypeId,
                )?.periodDuration;
                const titlePeriodDurationText = t(`Terms.ReportingType_${titlePeriodDuration}`, {
                    defaultValue: t("Terms.UnknownDuration"),
                });

                const isRenewable = isTermRenewable(periods, associatedTitle);

                return (
                    <TableRow
                        key={i}
                        className="associatedTitle-table-row"
                        onClick={() => handleShowDetails(associatedTitle as any)}
                    >
                        <TableCell>
                            <div className="associatedTitle">
                                <div
                                    className={`${
                                        isPreviousLineCompensationGroup ? "titles-compensation-group-link before" : ""
                                    } ${isNextLineCompensationGroup ? "titles-compensation-group-link after" : ""}`}
                                >
                                    <div
                                        className={
                                            associatedTitle.workId != null
                                                ? "associatedTitle-cover associatedTitle-cover--work"
                                                : "associatedTitle-cover"
                                        }
                                    >
                                        <TitleCover
                                            coverUrl={associatedTitle?.avatarUrl}
                                            title={associatedTitle.title.slice(0, 1)}
                                            style={{ minWidth: "32px", maxWidth: "32px" }}
                                        />
                                    </div>
                                </div>
                                <div className="associatedTitle-info">
                                    <span
                                        className={`associatedTitle-info-name${
                                            associatedTitle?.titleId ? " associatedTitle-info-name-exists" : ""
                                        }`}
                                        onClick={() => openRelatedResource(associatedTitle)}
                                    >
                                        {associatedTitle.title}
                                    </span>
                                    {associatedTitle?.representative && (
                                        <div className="associatedTitle-info-date">
                                            pour {associatedTitle?.representative}
                                        </div>
                                    )}
                                    <div className="associatedTitle-info-date">
                                        créé le :{" "}
                                        {formatLocaleDateString({
                                            date: associatedTitle.creationDate!,
                                            options: { numeric: true, timeZone: "UTC" },
                                        })}
                                    </div>
                                </div>
                            </div>
                        </TableCell>
                        <TableCell align="center" style={tBodyStyle}>
                            {associatedTitle?.reference ?? "-"}
                        </TableCell>
                        {usesAdvanceCompensation && (
                            <TableCell style={tBodyStyle} align="center">
                                {associatedTitle?.compensationGroupId ? compensationGroupName : "-"}
                            </TableCell>
                        )}
                        {usesMultiPeriod && (
                            <TableCell align="center" style={tBodyStyle}>
                                {titlePeriodDurationText}
                            </TableCell>
                        )}
                        <TableCell style={tBodyStyle} align="left">
                            {activePeriod && (
                                <TermExpirationStatusWrapper periods={periods} term={associatedTitle}>
                                    {associatedTitle?.endDate
                                        ? formatLocaleDateString({
                                              date: associatedTitle?.endDate,
                                              options: { numeric: true, timeZone: "UTC" },
                                          })
                                        : "-"}
                                </TermExpirationStatusWrapper>
                            )}
                        </TableCell>
                        <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                            {usesAgreements ? (
                                <ThreeDotMenu
                                    options={[
                                        {
                                            id: "create-avenant",
                                            name: t("ShowDetails.CreateAvenant"),
                                            action: () => handleCreateAvenant(associatedTitle as any),
                                        },
                                        ...(isRenewable
                                            ? [
                                                  {
                                                      id: "renew",
                                                      name: t("Terms.Renew"),
                                                      action: () => {
                                                          setCurrentTerm(associatedTitle);
                                                          setShowRenewTermModal(true);
                                                      },
                                                  },
                                              ]
                                            : []),
                                    ]}
                                />
                            ) : isRenewable ? (
                                <TextButton
                                    text={t("Terms.Renew")}
                                    handleClick={(e) => handleShowRenewTermModal(e, associatedTitle)}
                                    style={{ fontFamily: "Poppins, sans-serif", fontSize: "12px" }}
                                    underlineOnHover
                                />
                            ) : null}
                        </TableCell>
                    </TableRow>
                );
            });
        } else {
            return null;
        }
    }, [
        terms,
        renderButtons,
        activePeriod,
        handleShowDetails,
        handleShowRenewTermModal,
        openRelatedResource,
        navigate,
        periods,
        sortedTermsByGroupId,
        t,
        termGroups,
        usesAdvanceCompensation,
        usesMultiPeriod,
    ]);

    return (
        <div className="channel-table associatedTitles">
            {avenantTerm && (
                <AgreementModal
                    open={true}
                    article={avenantTerm}
                    onClose={() => setAvenantTerm(null)}
                    from="book"
                    relatedResource="term"
                    pageType="beneficiary"
                    initialDocumentType="avenant"
                />
            )}
            {currentTerm && (
                <RenewTermModal
                    open={showRenewTermModal}
                    onClose={() => setShowRenewTermModal(false)}
                    termId={currentTerm.id}
                    activePeriodStart={getRenewalPeriods(periods, currentTerm.periodTypeId).activePeriod?.periodStart}
                    relatedResourceId={currentTerm.beneficiarId}
                    relatedResource="beneficiary"
                />
            )}
            <ShowDetails
                currentTerm={currentTerm}
                currentBeneficiary={currentBeneficiary}
                isOpen={isOpen}
                setOpen={setIsOpen}
                deleteTerm={handleOpenDeleteModal}
                from={"beneficiars"}
                company={company}
                advancePayment={advancePaymentId}
            />
            <DeleteTermModal
                open={isDeleteTermModalOpen}
                onClose={() => setIsDeleteTermModalOpen(false)}
                onDelete={() => handleDeleteTerm(termIdToDelete as number)}
                resourceData={resourcePayment}
                termData={termToDelete}
            />
            <InformationTitle title={title} />
            <Paper
                sx={{
                    width: "100%",
                    overflow: "hidden",
                    marginBottom: "5px",
                    border: `1px solid ${variables["border-grey"]}`,
                    borderRadius: variables["border-raduis"],
                    boxShadow: "none",
                }}
            >
                <TableContainer>
                    <Table stickyHeader>
                        <TableHead>
                            <TableRow>
                                <TableCell sx={tHeadStyles}>{t("Beneficiars.AssociatedTitles")}</TableCell>
                                <TableCell sx={tHeadStyles} align="center">
                                    {t("Terms.Reference")}
                                </TableCell>
                                {usesAdvanceCompensation && (
                                    <TableCell sx={tHeadStyles} align="center">
                                        {t("CompensationGroups.Group")}
                                    </TableCell>
                                )}
                                {usesMultiPeriod && (
                                    <TableCell sx={tHeadStyles} align="center">
                                        {t("Terms.ReportingType")}
                                    </TableCell>
                                )}
                                <TableCell sx={tHeadStyles} align="left">
                                    {t("Terms.EndDate")}
                                </TableCell>
                                <TableCell sx={tHeadStyles}></TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {isTableLoading ? (
                                <TableSkeletonRows rowCount={4} columnCount={tableColumnCount} />
                            ) : (
                                renderTitles()
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Paper>
        </div>
    );
};

export default BeneficiariesTerms;
